import React from "react";
import PropTypes from "prop-types";
import { useQuery } from "@apollo/client";
import { css } from "@emotion/react";
import { FormattedMessage, useIntl } from "react-intl";
import { margins, colors, fontSize } from "style";
import { SAVE_INTERVIEW_SCHEDULING_SETTINGS } from "graphql/mutations";
import { GET_INTERVIEW_SCHEDULING_SETTING } from "graphql/queries";
import {
  EVENT_DURATION_OPTIONS,
  DEFAULT_DURATION_MINUTES,
  DEFAULT_AVAILABILITY,
  EVENT_NAME_MAX_LENGTH,
  INTERVIEW_VIDEO_PROVIDERS,
  isInterviewVideoProviderConnected,
  isValidInterviewAvailability,
} from "constants/index";
import { defaultTimezone } from "utils";
import { useToast, useLinks, useInterviewSchedulingLocationOptions, MUTATION_TYPE } from "hooks";
import Form, { Label, SIZE, TYPE } from "components/Form";
import Spinner from "components/Loader/Spinner";
import { PrimaryLink } from "components/Links";
import { MessagesError } from "components/Messages";
import { ActionButton, SecondaryButton } from "components/Buttons";
import Icon, { ICON_TYPE } from "components/Icon";
import Tooltip from "components/Tooltip";
import { INTERVIEW_EVENT_STATUS, INTERVIEW_EVENT_STATUS_MESSAGES } from "constants/calendly";

/**
 * Calendly scheduling settings form component.
 *
 * @param {Boolean} preview Show the default settings faded and read-only, without loading anything from Calendly
 * @returns {JSX.Element} The Calendly scheduling settings form component.
 */
const CalendlySchedulingSettings = ({ preview = false }) => {
  const { toast } = useToast();
  const intl = useIntl();
  const links = useLinks();
  const { data, loading, error, refetch } = useQuery(GET_INTERVIEW_SCHEDULING_SETTING, {
    skip: preview,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "network-only",
  });
  const setting = data?.interviewSchedulingSetting;
  const locationOptions = useInterviewSchedulingLocationOptions({ skip: preview });

  if (loading) return <Spinner size="2rem" margin="1rem auto" />;
  if (error) {
    return (
      <div role="alert">
        <MessagesError
          messageId={
            needsReconnect(error)
              ? "UserSettings.InterviewScheduling.Location.ReconnectHelp"
              : "UserSettings.InterviewScheduling.Settings.LoadError"
          }
        />
        {needsReconnect(error) ? (
          <PrimaryLink to={links.calendly_connect}>
            <FormattedMessage id="UserSettings.InterviewScheduling.Location.Reconnect" />
          </PrimaryLink>
        ) : (
          <SecondaryButton type="button" onClick={() => refetch().catch(() => {})}>
            <FormattedMessage id="UserSettings.InterviewScheduling.Location.Retry" />
          </SecondaryButton>
        )}
      </div>
    );
  }

  const eventMessageId = INTERVIEW_EVENT_STATUS_MESSAGES[setting?.eventStatus];
  const recreateEvent = setting?.eventStatus === INTERVIEW_EVENT_STATUS.missing;
  const managedInCalendly = [INTERVIEW_EVENT_STATUS.inactive, INTERVIEW_EVENT_STATUS.unsupported].includes(
    setting?.eventStatus
  );
  const initialValues = setting
    ? {
        ...(recreateEvent && { recreateEvent: true }),
        eventName: setting.eventName,
        durationMinutes: setting.durationMinutes,
        timezone: setting.timezone || defaultTimezone(),
        videoProvider: setting.videoProvider,
        availability: setting.availability.map(({ weekday, working, dayStartsAt, dayEndsAt }) => ({
          weekday,
          working,
          dayStartsAt,
          dayEndsAt,
        })),
      }
    : {
        eventName: intl.formatMessage({ id: "UserSettings.InterviewScheduling.Settings.DefaultEventName" }),
        durationMinutes: DEFAULT_DURATION_MINUTES,
        timezone: defaultTimezone(),
        videoProvider: "",
        availability: DEFAULT_AVAILABILITY,
      };
  const providerOptions = Object.values(INTERVIEW_VIDEO_PROVIDERS)
    .filter((provider) => preview || isInterviewVideoProviderConnected(provider, locationOptions.options))
    .map((provider) => ({
      value: provider,
      label: intl.formatMessage({ id: `UserSettings.InterviewScheduling.Location.Provider.${provider}` }),
    }));

  return (
    <>
      {eventMessageId && (
        <p role="status">
          <FormattedMessage id={eventMessageId} />
        </p>
      )}
      {managedInCalendly ? (
        <PrimaryLink to="https://calendly.com" target="_blank" rel="noreferrer">
          <FormattedMessage id="UserSettings.InterviewScheduling.Settings.OpenCalendly" />
        </PrimaryLink>
      ) : (
        <div css={preview && styles.preview} {...(preview && { inert: "", "aria-disabled": true })}>
          <Form
            key={`${setting?.id}-${setting?.eventStatus}`}
            data={getFormData({ ...locationOptions, providerOptions, reconnectUrl: links.calendly_connect }, intl)}
            initialValues={initialValues}
            marginSize={margins.none}
            mutationType={MUTATION_TYPE.add}
            mutationData={{
              mutationName: { add: SAVE_INTERVIEW_SCHEDULING_SETTINGS },
              updateCache: (cache, { data }) => {
                const savedSetting = data?.saveInterviewSchedulingSettings?.interviewSchedulingSetting;
                if (savedSetting) {
                  cache.writeQuery({
                    query: GET_INTERVIEW_SCHEDULING_SETTING,
                    data: { interviewSchedulingSetting: savedSetting },
                  });
                }
              },
            }}
            onCompleted={() =>
              toast.success(<FormattedMessage id="UserSettings.InterviewScheduling.Settings.SaveSuccess" />)
            }
            onError={(error) => {
              toast.error(<FormattedMessage id="UserSettings.InterviewScheduling.Settings.SaveError" />);
              if (needsReconnect(error)) {
                locationOptions.refresh();
              }
            }}
            saveButton={{
              show: !preview,
              labelId: recreateEvent ? "UserSettings.InterviewScheduling.Settings.RecreateEvent" : "Global.Save",
            }}
          />
        </div>
      )}

      {setting?.ready && setting.schedulingUrl && (
        <PrimaryLink to={setting.schedulingUrl} target="_blank" rel="noreferrer" css={styles.link}>
          <FormattedMessage id="UserSettings.InterviewScheduling.Settings.ViewSchedulingPage" />
        </PrimaryLink>
      )}
    </>
  );
};

/**
 * MeetingPlatformHeader: field label, help, and connection refresh.
 * @param {Boolean} loading Whether connections are being checked
 * @param {Function} refresh Refreshes connected platforms
 */
const MeetingPlatformHeader = ({ loading, refresh }) => {
  const intl = useIntl();
  return (
    <div css={styles.fieldHeader}>
      <Label htmlFor="videoProvider" css={styles.fieldLabel}>
        <FormattedMessage id="UserSettings.InterviewScheduling.Settings.MeetingPlatform" />
      </Label>
      <div css={styles.actions}>
        <Tooltip
          tooltipId="UserSettings.InterviewScheduling.Settings.MeetingPlatformGuidance"
          values={{
            help: intl.formatMessage({ id: "UserSettings.InterviewScheduling.Location.VideoHelp" }),
            sharedDefault: intl.formatMessage({ id: "UserSettings.InterviewScheduling.Location.SharedDefault" }),
          }}
          hideIcon
        >
          <ActionButton
            css={styles.iconButton}
            aria-label={intl.formatMessage({ id: "UserSettings.InterviewScheduling.Settings.MeetingPlatformHelp" })}
          >
            <Icon type={ICON_TYPE.info} aria-hidden="true" />
          </ActionButton>
        </Tooltip>
        <ActionButton
          css={styles.iconButton}
          onClick={() => refresh()}
          disabled={loading}
          aria-label={intl.formatMessage({ id: "UserSettings.InterviewScheduling.Location.Refresh" })}
          title={intl.formatMessage({ id: "UserSettings.InterviewScheduling.Location.Refresh" })}
        >
          <Icon type={ICON_TYPE.refresh} aria-hidden="true" />
        </ActionButton>
      </div>
    </div>
  );
};

MeetingPlatformHeader.propTypes = {
  loading: PropTypes.bool.isRequired,
  refresh: PropTypes.func.isRequired,
};

/**
 * MeetingPlatformStatus: connection and selection feedback.
 * @param {Boolean} loading Whether validation is pending
 * @param {Object} error Connection query error
 * @param {Array} providerOptions Supported connected platforms
 * @param {String} selectedProvider Current form selection
 * @param {String} reconnectUrl Calendly authorization URL
 */
const MeetingPlatformStatus = ({ loading, error, providerOptions, selectedProvider, reconnectUrl }) => {
  let messageId;
  if (loading) messageId = "UserSettings.InterviewScheduling.Location.Loading";
  else if (error)
    messageId = needsReconnect(error)
      ? "UserSettings.InterviewScheduling.Location.ReconnectHelp"
      : "UserSettings.InterviewScheduling.Location.LoadError";
  else if (!providerOptions.length) messageId = "UserSettings.InterviewScheduling.Settings.NoConnectedPlatforms";
  else if (selectedProvider && !providerOptions.some(({ value }) => value === selectedProvider))
    messageId = "UserSettings.InterviewScheduling.Settings.DisconnectedPlatform";
  else return null;

  return (
    <div css={styles.help} role="status" aria-live="polite">
      <FormattedMessage id={messageId} />
      {!loading && needsReconnect(error) && (
        <PrimaryLink to={reconnectUrl}>
          <FormattedMessage id="UserSettings.InterviewScheduling.Location.Reconnect" />
        </PrimaryLink>
      )}
    </div>
  );
};

MeetingPlatformStatus.propTypes = {
  loading: PropTypes.bool.isRequired,
  error: PropTypes.object,
  providerOptions: PropTypes.array.isRequired,
  selectedProvider: PropTypes.string,
  reconnectUrl: PropTypes.string.isRequired,
};

const decorateMeetingPlatform = (Component, { loading, error, providerOptions, refresh, reconnectUrl }) =>
  function renderMeetingPlatform(props) {
    return (
      <div css={styles.platformField}>
        <MeetingPlatformHeader loading={loading} refresh={refresh} />
        <Component {...props} />
        <MeetingPlatformStatus
          loading={loading}
          error={error}
          providerOptions={providerOptions}
          selectedProvider={props.formData.videoProvider}
          reconnectUrl={reconnectUrl}
        />
      </div>
    );
  };

const getFormData = (locationOptions, intl) => [
  {
    items: [
      {
        decorator: { type: decorateMeetingPlatform, props: locationOptions },
        type: TYPE.select,
        size: SIZE.medium,
        disabled: locationOptions.loading || !!locationOptions.error,
        onChange: (name, value, handleChange) => {
          const focused = document.activeElement?.id === name;
          handleChange(name, value);
          // Select keys its input by value, so choosing a platform remounts that input.
          if (focused) requestAnimationFrame(() => document.getElementById(name)?.focus());
        },
        properties: {
          name: "videoProvider",
          inputId: "videoProvider",
          required: true,
          isClearable: false,
          options: locationOptions.providerOptions,
          placeholder: intl.formatMessage({ id: "UserSettings.InterviewScheduling.Settings.SelectMeetingPlatform" }),
          noOptionsMessage: "UserSettings.InterviewScheduling.Settings.NoConnectedPlatforms",
          validate: (value) => locationOptions.providerOptions.some((option) => option.value === value),
        },
      },
      {
        label: <FormattedMessage id="UserSettings.InterviewScheduling.Settings.EventName" />,
        type: TYPE.input,
        size: SIZE.medium,
        properties: {
          name: "eventName",
          required: true,
          maxLength: EVENT_NAME_MAX_LENGTH,
          validate: (value) => Boolean(value?.trim()),
        },
      },
      {
        label: <FormattedMessage id="UserSettings.InterviewScheduling.Settings.Duration" />,
        type: TYPE.radio,
        size: SIZE.medium,
        valueFunc: Number,
        properties: {
          name: "durationMinutes",
          variant: "button",
          options: EVENT_DURATION_OPTIONS,
          required: true,
        },
      },
      {
        type: TYPE.schedule,
        size: SIZE.xlarge,
        properties: {
          name: "availability",
          required: true,
          validate: isValidInterviewAvailability,
        },
      },
    ],
  },
];

const needsReconnect = (error) =>
  Boolean(error?.graphQLErrors?.some(({ extensions }) => extensions?.code === "CALENDLY_RECONNECT_REQUIRED"));

const styles = {
  preview: css`
    opacity: 0.6;
    filter: saturate(0.4);
    user-select: none;
  `,
  help: css`
    margin-top: 0.5rem;
    display: flex;
    flex-direction: column;
    gap: ${margins.xxsmall};
    color: ${colors.grayAnatomyBase};
    font-size: ${fontSize.xsmall};
  `,
  platformField: css`
    display: flex;
    flex-direction: column;
    min-width: 0;
    order: 1;
  `,
  fieldHeader: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${margins.xxsmall};
    margin-top: 0.75rem;
    margin-bottom: 0.5rem;
  `,
  fieldLabel: css`
    margin-bottom: 0;
  `,
  actions: css`
    display: flex;
    align-items: center;
    gap: ${margins.xxsmall};
  `,
  iconButton: css`
    height: 2rem;
    padding: 0.2rem !important;
  `,
  link: css`
    display: block;
    margin-top: 1.5rem;
  `,
};

CalendlySchedulingSettings.propTypes = {
  preview: PropTypes.bool,
};

export default CalendlySchedulingSettings;
